<script setup lang="ts">
import { inject, onMounted, type Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-fomantic-ui'
import { useContainerStore } from '@/stores/containers'
import { useSyncStore } from '@/stores/sync'
import { ContainerStatus, type Container } from '@/models/Container'
import type { Room } from '@/models/Room'
import { ScanState, ScanTarget } from '@/models/Scan'
import { ActionType } from '@/actions/Action'
import { ContainerAction } from '@/actions/ContainerMove'
import Title from '@/components/Title.vue'
import Scanner from '@/components/Scanner/Scanner.vue'
import FadeTransition from '@/components/FadeTransition.vue'
import RoomHeading from '@/components/Heading/RoomHeading.vue'
import ContainerScanHeading from '@/components/Heading/ContainerScanHeading.vue'
import Actions from '../components/Actions.vue'

const router = useRouter()
const route = useRoute()
const { toast } = useToast()

const containerStore = useContainerStore()
const syncStore = useSyncStore()

const room = inject('room') as Ref<Room | null>
const container = inject('container') as Ref<Container | null>
const action = inject('action') as Ref<ContainerAction | null>

let syncInProgress = false

const sync = async () => {
  if (!room.value || !container.value || !action.value || syncInProgress) return
  syncInProgress = true

  await syncStore.syncAction({
    type: ActionType.ContainerMove,
    containerCode: container.value.code,
    payload: {
      action: action.value,
      roomUuid: room.value.uuid,
    },
  })

  const messages = {
    DROP: `Le bac ${container.value.code} a été déposé.`,
    PULL: `Le bac ${container.value.code} a été retiré.`,
    EMPTY: `Le bac ${container.value.code} a été déclaré vide.`,
  }

  toast({
    position: 'centered',
    message: messages[action.value],
    type: 'success',
  })

  if (action.value === ContainerAction.Drop) {
    container.value.status = ContainerStatus.Assigned
  } else if (action.value === ContainerAction.Pull) {
    container.value.status = ContainerStatus.AwaitingWeighing
  }
  container.value.roomUuid = room.value.uuid

  container.value = null
  action.value = null

  syncInProgress = false
}

const barcodeScanned = async (value: string) => {
  container.value = containerStore.findByCode(value)

  if (!container.value) {
    toast({
      position: 'centered',
      message: `Le code barre scanné n'est pas celui d'un bac.`,
      type: 'warning',
    })
    return
  }

  if (!action.value) {
    toast({
      position: 'centered',
      message: `Veuillez sélectionner une action.`,
      type: 'warning',
    })
    return
  }

  if (
    container.value.status === ContainerStatus.Assigned &&
    action.value === ContainerAction.Drop
  ) {
    router.replace({ name: 'move-container-assigned' })
    return
  }

  if (container.value.status === ContainerStatus.AwaitingWeighing) {
    router.replace({ name: 'move-container-awaiting-weighing' })
    return
  }

  sync()
}

onMounted(() => {
  if (route.query.sync) {
    sync()
  }
})
</script>

<template>
  <div class="page move-container-container">
    <Title>Retirer / déposer un bac</Title>
    <FadeTransition>
      <ContainerScanHeading v-if="action"></ContainerScanHeading>
      <RoomHeading v-else></RoomHeading>
    </FadeTransition>
    <Scanner
      :target="!action ? ScanTarget.Room : ScanTarget.Container"
      :state="!action ? ScanState.Scanned : ScanState.Scanning"
      @barcode-scanned="barcodeScanned"
    ></Scanner>
    <Actions @action-selected="action = $event"></Actions>
  </div>
</template>
